<template>
  <!-- Wareneingang mobil (Trello 21.08.): funktional wie der Desktop-Dialog, alle Felder Pflicht -->
  <div class="m-wms-form">
    <div class="m-wms-form__row">
      <div class="m-wms-form__field">
        <label>{{ $t("mobile.wms.sku") }} *</label>
        <input v-model="skuQuery" :placeholder="$t('mobile.wms.skuPlaceholder')" @input="onSkuInput" @focus="onSkuInput" />
      </div>
      <m-wms-photo-button kind="ean" @extracted="onPhotoResult" @extract-error="skuError = $t('mobile.wms.photoReadError')" />
      <button type="button" class="m-wms-form__iconbtn" @click="scannerOpen = true"><i class="material-icons">qr_code_scanner</i></button>
    </div>
    <ul v-if="skuSuggestions.length" class="m-wms-suggest">
      <li v-for="p in skuSuggestions" :key="p.sku" @click="chooseSku(p)">{{ p.sku }} · {{ p.name }}</li>
    </ul>
    <p v-if="skuError" class="m-wms-form__error">{{ skuError }}</p>
    <p v-else-if="skuInfo" class="m-hint m-hint--small">{{ skuInfo }}</p>

    <div class="m-wms-form__field" @click="numPadOpen = true">
      <label>{{ $t("mobile.wms.quantity") }} *</label>
      <input :value="quantity || ''" readonly :placeholder="$t('mobile.wms.quantityPlaceholder')" />
    </div>

    <m-wms-location-field v-model="location" :label="$t('mobile.wms.location')" />

    <div class="m-wms-form__field">
      <label>{{ $t("mobile.wms.reason") }} *</label>
      <input v-model="reason" :placeholder="$t('mobile.wms.reasonPlaceholder')" />
    </div>

    <div class="m-wms-actions">
      <button class="m-wms-actions__cancel" @click="$router.push('/m/lager')">{{ $t("mobile.wms.cancel") }}</button>
      <button class="m-wms-actions__save" :disabled="!canSave || saving" @click="save">{{ $t("mobile.wms.save") }}</button>
    </div>

    <m-num-pad v-if="numPadOpen" :title="$t('mobile.wms.quantity')" :initial="quantity" @confirm="quantity = $event" @close="numPadOpen = false" />
    <m-barcode-scanner v-if="scannerOpen" @scanned="onScanned" @close="scannerOpen = false" />
  </div>
</template>

<script>
import axiosFactory from "@/services/axios.factory";
import WarehouseService from "@/services/warehouse.service";
import MNumPad from "../components/MNumPad.vue";
import MBarcodeScanner from "../components/MBarcodeScanner.vue";
import MWmsLocationField from "./MWmsLocationField.vue";
import MWmsPhotoButton from "./MWmsPhotoButton.vue";
import { findByGtin, isGtin } from "@/helpers/gtin";

export default {
  name: "MWareneingang",
  components: { MNumPad, MBarcodeScanner, MWmsLocationField, MWmsPhotoButton },
  data() {
    return {
      products: [],
      skuQuery: "",
      skuSuggestions: [],
      selectedSku: null,
      skuError: null,
      skuInfo: null,
      quantity: null,
      location: null,
      reason: "",
      numPadOpen: false,
      scannerOpen: false,
      saving: false,
    };
  },
  computed: {
    canSave() {
      return !!(this.selectedSku && this.quantity > 0 && this.location && this.reason.trim());
    },
  },
  async created() {
    // Artikelstamm einmal laden, lokal suchen (Lager-WLAN) - gleiches Muster wie die Retourenerfassung
    const res = await axiosFactory().get("/shipping_data/products/lookup");
    this.products = res.data.data || [];
  },
  methods: {
    // GTIN (Scanner tippt sie ins Feld) -> passende SKU uebernehmen (Trello WMS mobil, Update 27.08.)
    resolveGtin(code) {
      const hit = findByGtin(this.products, code);
      if (!hit) return false;
      this.chooseSku(hit);
      this.skuInfo = this.$t("mobile.wms.gtinResolved", { gtin: String(code).trim(), sku: hit.sku });
      return true;
    },
    onSkuInput() {
      this.skuError = null;
      this.skuInfo = null;
      this.selectedSku = null;
      if (this.resolveGtin(this.skuQuery)) return;
      const q = this.skuQuery.trim().toUpperCase();
      this.skuSuggestions = q.length < 2 ? [] : this.products.filter((p) => (p.sku || "").toUpperCase().includes(q) || (p.name || "").toUpperCase().includes(q)).slice(0, 12);
      // Exakter Treffer gilt sofort als Auswahl
      const exact = this.products.find((p) => (p.sku || "").toUpperCase() === q);
      if (exact) this.selectedSku = exact.sku;
    },
    chooseSku(p) {
      this.skuQuery = p.sku;
      this.selectedSku = p.sku;
      this.skuSuggestions = [];
    },
    onPhotoResult(r) {
      // Vision liest den SKU-Aufdruck vom Sticker; nur eine bekannte SKU wird uebernommen
      this.skuError = null;
      this.skuInfo = null;
      this.selectedSku = null; // alte Auswahl darf nicht stehen bleiben (KAST6-Vorfall 28.08.)
      const sku = r && r.sku ? String(r.sku).toUpperCase() : null;
      const hit = sku ? this.products.find((p) => (p.sku || "").toUpperCase() === sku) : null;
      if (hit) {
        this.chooseSku(hit);
      } else if (sku) {
        this.skuQuery = r.sku;
        this.selectedSku = null;
        this.skuSuggestions = [];
        this.skuError = this.$t("mobile.wms.skuScanUnknown", { code: r.sku });
      } else {
        this.skuError = this.$t("mobile.wms.photoReadFailed");
      }
    },
    onScanned({ value }) {
      // EAN-13 -> SKU ueber die GTIN im Artikelstamm; sonst muss der Code selbst eine bekannte SKU sein
      this.scannerOpen = false;
      this.skuError = null;
      this.skuInfo = null;
      this.selectedSku = null; // alte Auswahl darf nicht stehen bleiben (KAST6-Vorfall 28.08.)
      const code = String(value || "")
        .trim()
        .toUpperCase();
      if (this.resolveGtin(code)) return;
      const hit = this.products.find((p) => (p.sku || "").toUpperCase() === code);
      if (hit) {
        this.chooseSku(hit);
      } else {
        this.skuQuery = code;
        this.skuError = isGtin(code) ? this.$t("mobile.wms.gtinUnknown", { code }) : this.$t("mobile.wms.skuScanUnknown", { code });
      }
    },
    async save() {
      if (!this.canSave || this.saving) return;
      this.saving = true;
      try {
        await WarehouseService.addIncomingGoods({
          sku: this.selectedSku,
          storageLocationId: this.location.id,
          inventory: this.quantity,
          reason: this.reason.trim(),
          source: this.reason.trim(),
        });
        this.$notify({ timeout: 2500, message: this.$t("wms.messages.created"), horizontalAlign: "right", verticalAlign: "top", type: "success" });
        this.$router.push("/m/lager");
      } catch (err) {
        // Server lehnt Artikel ab, die nicht in "Produkte" angelegt sind
        const reason = err && err.response && err.response.data && err.response.data.reason;
        if (reason === "unknown_sku") {
          this.skuError = this.$t("mobile.wms.skuNotInProducts", { sku: this.selectedSku });
          this.selectedSku = null;
          return;
        }
        this.$notify({ timeout: 3500, message: this.$t("mobile.wms.saveError"), horizontalAlign: "right", verticalAlign: "top", type: "danger" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./m-wms.scss"></style>
